'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { VariantMapped } from '@/types/product'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProductInfo } from '../product-info/module'
import { ProductInfoSuggest } from './module'
import { ImageType } from './product-info-suggest-gallery'

// Định nghĩa kiểu dữ liệu linh hoạt hơn cho sản phẩm
interface ColorOption {
  title: string
  slug: string
  label: string
  image: Array<{ id: string; src: string }>
}

interface OptionsValue {
  title: string
  options_id: string
  options: ColorOption[]
  values: string[]
}

interface BasicProductInfo {
  id: string
  display_name: string
  seo_alias: string
  regular_price: number
  compare_price?: number
  percent?: number
  default_thumbnail: string
  mapped_variants?: Record<string, VariantMapped[]>
  images?: Array<{ src: string; id?: string }>
  options_value?: OptionsValue[]
  [key: string]: unknown
}

interface ProductInfoSuggestModalProps {
  product?: BasicProductInfo
}

export const ProductInfoSuggestModal = ({
  product,
}: ProductInfoSuggestModalProps) => {
  if (!product) return null

  return (
    <ResponsiveModal>
      <ResponsiveModalTrigger asChild>
        <Button
          variant={'outline-neutral'}
          size={'icon'}
          className='group h-9 w-[42px] lg:w-12.5'
          data-tracking-level-3={'ViewModal'}
        >
          <Image
            src={'/icons/icon-add-cart.svg'}
            width={18}
            height={20}
            alt='add-cart'
            className='group-hover:brightness-0 group-hover:invert'
          />
        </Button>
      </ResponsiveModalTrigger>
      <ModalContent product={product} />
    </ResponsiveModal>
  )
}

// Tách nội dung của modal ra một component riêng để tránh hydration mismatch
const ModalContent = ({ product }: { product: BasicProductInfo }) => {
  // State cho màu và size được chọn
  const [selectedColorId, setSelectedColorId] = useState<string>('')
  // Xử lý ảnh gallery dựa trên màu được chọn
  const galleryImages: ImageType[] = useMemo(() => {
    if (!product?.options_value?.[0] || !selectedColorId) return []

    // Find the selected color in options_value
    const colorOption = product.options_value[0].options.find(
      (opt: ColorOption) => opt.title === selectedColorId
    )

    // Use color-specific images if available, otherwise fallback to product images
    const images = colorOption?.image || product?.images || []

    // Exclude last image from array
    const galleryImages = images.slice(0, -1)

    return galleryImages.map((img: { src: string; id?: string }) => ({
      url: img.src,
      alt: product.display_name || 'Product image',
    }))
  }, [
    product?.options_value,
    product?.images,
    selectedColorId,
    product?.display_name,
  ])

  // Lấy mảng màu sắc từ options_value
  const colorVariants = useMemo(() => {
    if (!product?.mapped_variants) return []

    // Lấy các màu duy nhất
    const colors = Object.keys(product.mapped_variants || {})

    // Tạo mảng variant từ các màu
    return colors.map((color) => {
      const firstVariant = product.mapped_variants?.[color]?.[0]
      return {
        imageUrl: firstVariant?.colorImage?.toString() || '',
        title: color,
        id: color,
      }
    })
  }, [product?.mapped_variants])

  // Update selectedColorId when variants change
  useEffect(() => {
    if (colorVariants.length > 0 && !selectedColorId) {
      setSelectedColorId(colorVariants[0].id)
    }
  }, [colorVariants, selectedColorId])

  // Lấy các size phù hợp với màu đã chọn và thông tin isDisable
  const availableSizes = useMemo(() => {
    if (!product?.mapped_variants || !selectedColorId) return []

    const variantsForSelectedColor =
      product.mapped_variants[selectedColorId] || []

    // Map để lưu trữ thông tin size: {[size]: {isDisabled, variant}}
    const sizeMap = new Map<
      string,
      { isDisabled: boolean; variant: VariantMapped }
    >()

    // Xử lý từng variant để lấy thông tin size
    variantsForSelectedColor.forEach((variant) => {
      if (!variant.size) return

      const sizeKey = variant.size.toString()

      // Nếu size chưa có trong map hoặc variant hiện tại available nhưng variant đã lưu không available
      if (
        !sizeMap.has(sizeKey) ||
        (!variant.isDisable && sizeMap.get(sizeKey)?.isDisabled)
      ) {
        sizeMap.set(sizeKey, {
          isDisabled: variant.isDisable || false,
          variant,
        })
      }
    })

    // Định nghĩa thứ tự kích thước
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]))

    // Chuyển đổi map thành mảng đối tượng và sắp xếp theo thứ tự kích thước
    return Array.from(sizeMap.entries())
      .map(([size, data]) => ({
        title: size,
        id: size.toLowerCase(),
        isDisabled: data.isDisabled,
      }))
      .sort((a, b) => {
        const indexA = sizeOrderMap.get(a.title) ?? Infinity
        const indexB = sizeOrderMap.get(b.title) ?? Infinity
        return indexA - indexB
      })
  }, [product?.mapped_variants, selectedColorId])

  // State cho size được chọn
  const [selectedSizeId, setSelectedSizeId] = useState<string>('')

  // Cập nhật selectedSizeId khi availableSizes thay đổi
  useMemo(() => {
    // Tìm size đầu tiên không bị disabled
    const firstAvailableSize = availableSizes.find((size) => !size.isDisabled)

    // Nếu size hiện tại không hợp lệ (không tồn tại hoặc bị disabled), chọn size đầu tiên có sẵn
    if (
      availableSizes.length > 0 &&
      (!selectedSizeId ||
        !availableSizes.some((s) => s.id === selectedSizeId && !s.isDisabled))
    ) {
      setSelectedSizeId(
        firstAvailableSize ? firstAvailableSize.id : availableSizes[0].id
      )
    }
  }, [availableSizes, selectedSizeId])

  // Handlers
  const handleColorChange = useCallback((colorId: string) => {
    setSelectedColorId(colorId)
  }, [])

  const handleSizeChange = useCallback((sizeId: string) => {
    setSelectedSizeId(sizeId)
  }, [])

  return (
    <ResponsiveModalContent
      className='z-[1000] gap-0 border-none px-7 py-10 lg:w-[1064px] lg:max-w-full lg:rounded-md'
      aria-describedby={'modal-add-suggest'}
    >
      <ResponsiveModalTitle className='mb-4.5 font-criteria text-2xl font-semibold leading-7 text-primary lg:text-[28px] lg:leading-9.5'>
        Mua cùng giảm thêm 5%
      </ResponsiveModalTitle>
      <ResponsiveModalDescription />
      <div className='items-stretch gap-11 lg:flex'>
        <ProductInfoSuggest.Gallery
          images={galleryImages}
          className='h-[200px] basis-1/2 max-lg:hidden md:flex md:gap-4 lg:h-full [&_.pswp-gallery]:h-full'
        />
        <div className={'flex basis-1/2 flex-col gap-2 bg-light lg:p-0'}>
          <div>
            <div className='flex items-start gap-4'>
              <Image
                src={ImageHelper.getImageUrl(product?.default_thumbnail || '')}
                alt={product?.display_name || 'Product'}
                width={68}
                height={68}
                className='rounded lg:hidden'
              />{' '}
              <div>
                <h1 className='line-clamp-2 font-sans text-lg font-medium leading-6 text-neutral-900 lg:mb-1 lg:text-[28px] lg:leading-9'>
                  {product?.display_name}
                </h1>
                {/* <p className='text-sm leading-4.5 text-neutral-900/60 lg:text-base lg:leading-5.5'>
                  Thoáng khí
                </p> */}
              </div>
            </div>

            <Divider className='my-5 bg-neutral-200' />
            <ProductInfo.Price
              comparePrice={product?.compare_price || 0}
              regularPrice={product?.regular_price || 0}
              salePercent={product?.percent || 0}
            />
          </div>

          <div className='space-y-4 lg:mt-10'>
            <ProductInfoSuggest.Variants
              variants={colorVariants}
              selectedVariantId={selectedColorId}
              onVariantChange={handleColorChange}
            />
            <ProductInfoSuggest.Sizes
              sizes={availableSizes}
              selectedSizeId={selectedSizeId}
              onSizeChange={handleSizeChange}
            />
            <ProductInfoSuggest.AddCart
              size={selectedSizeId}
              color={selectedColorId}
              product={product}
            />
          </div>
        </div>
      </div>
    </ResponsiveModalContent>
  )
}
